import * as React from "react";

import Form, { FormComponentProps } from "@ant-design/compatible/es/form";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import FormCard from "@impero-com/common/components/formCard";
import FormCardContainer from "@impero-com/common/components/formCardContainer";
import RightSideIcon from "@impero-com/common/components/rightSideIcon";
import {
    withRouter,
    WithRouterProps,
} from "@impero-com/common/components/routerUtils";
import linkBrokenSvg from "@impero-com/common/img/link-broken.svg";
import passwordSvg from "@impero-com/common/img/password-login.svg";
import { AjaxManager } from "@impero-com/common/utils/ajax";
import { isUnauthenticated } from "@impero-com/common/utils/auth";
import {
    buildUserDictionary,
    confirmNormalPassword,
    Callback,
} from "@impero-com/common/utils/confirmPassword";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { OrgCookie } from "@impero-com/common/utils/orgCookie";
import {
    Button,
    emitNotification,
    CardFooter,
    FormTitle,
    FormSubtitle,
} from "@impero-com/ui";
import { Input, Popover, Spin } from "antd";
import { Link } from "react-router-dom";

import {
    getResetPassword,
    postResetPassword,
} from "api/generated/authentication/authentication";
import { Authenticated, UserView } from "api/generated/model";

import { ENVIRONMENT } from "../../env";
import {
    LOGIN_STATE_PARAM,
    LOGIN_STATE_SETUP_2FA,
    LOGIN_STATE_TWO_FACTOR,
    CHALLENGE,
} from "../loginTypeSwitch";

import * as theme from "./theme.css";

const FormItem = Form.Item;

class ResetPasswordInternal extends React.Component<
    ResetPasswordProps,
    ResetPasswordState
> {
    private token: string | null;

    constructor(props: ResetPasswordProps) {
        super(props);
        this.state = {
            confirmDirty: false,
            status: { type: "DEFAULT" },
        };
        const searchParams = new URLSearchParams(window.location.search);
        this.token = searchParams.get("token");
    }

    componentDidMount() {
        this.fetchUser();
    }

    fetchUser = () => {
        const { ajaxManager } = this.props;
        ajaxManager
            .ajax<UserView, ResetPasswordStatus>(
                () => getResetPassword({ token: this.token ?? "" }),
                {
                    component: this,
                    initialStatusValue: "DEFAULT",
                    getSuccessStatus: () => ({ type: "DEFAULT" }),
                    getErrorStatus: () => ({ type: "DEFAULT" }),
                    inFlightStatus: { type: "LOGGING_IN" },
                    onErrorCode: {
                        422: () =>
                            this.setState({
                                status: { type: "INVALID_TOKEN" },
                            }),
                    },
                }
            )
            .then((user) =>
                this.setState({
                    user: {
                        email: user.email,
                        firstName: user.firstname,
                        lastName: user.lastname,
                    },
                })
            );
    };

    handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        this.props.form.validateFields(
            (err: any, values: ResetPasswordProps) => {
                if (!err) {
                    const humanVerificationResponse: string | undefined =
                        values.captcha;
                    const { password } = values;
                    const { ajaxManager, navigate } = this.props;
                    ajaxManager
                        .ajax<Authenticated, ResetPasswordStatus>(
                            () =>
                                postResetPassword(
                                    {
                                        password: password ?? "",

                                        human_verification_response:
                                            humanVerificationResponse ?? "",
                                    },
                                    { token: this.token ?? "" }
                                ),
                            {
                                component: this,
                                initialStatusValue: "DEFAULT",
                                getErrorStatus: () => ({ type: "DEFAULT" }),
                                getSuccessStatus: () => ({ type: "DEFAULT" }),
                                inFlightStatus: { type: "LOGGING_IN" },
                                onErrorCode: {
                                    400: () => {
                                        emitNotification(
                                            "error",
                                            t(
                                                "ChangePassword_NewPassword_Invalid"
                                            )
                                        );
                                    },
                                    401: (e) => {
                                        const data = e.response?.data;
                                        if (!isUnauthenticated(data)) {
                                            throw new Error(
                                                t("Login_UnknownAuthType")
                                            );
                                        }
                                        switch (data.status) {
                                            case "notAuthenticated":
                                            case "phoneNotVerified":
                                                // The registration should already have passed 1FA
                                                throw new Error(
                                                    "Registration failed!"
                                                );
                                            case "setupRequired":
                                                navigate(
                                                    `/?${LOGIN_STATE_PARAM}=${encodeURIComponent(
                                                        LOGIN_STATE_SETUP_2FA
                                                    )}&${CHALLENGE}=${encodeURIComponent(
                                                        data.challenge ?? ""
                                                    )}`
                                                );
                                                break;
                                            case "pinRequired":
                                                navigate(
                                                    `/?${LOGIN_STATE_PARAM}=${encodeURIComponent(
                                                        LOGIN_STATE_TWO_FACTOR
                                                    )}&${CHALLENGE}=${encodeURIComponent(
                                                        data.challenge ?? ""
                                                    )}`
                                                );
                                                break;
                                            default:
                                                return assertNever(data);
                                        }
                                    },
                                    404: () => {
                                        emitNotification(
                                            "error",
                                            t("ResetPassword_LinkIsIncorrect")
                                        );
                                    },
                                },
                            }
                        )
                        .then(() => window.location.assign("/"));
                }
            }
        );
    };

    handleConfirmPassword = (rule: any, value: string, callback: Callback) => {
        const userDictionary = buildUserDictionary(
            this.props.orgCookie,
            this.state.user
        );
        confirmNormalPassword(rule, value, callback, userDictionary);
        this.validateToNextPassword(rule, value, callback);
    };

    compareToFirstPassword = (
        rule: any,
        value: string,
        callback: (msg?: string) => void
    ) => {
        const { form } = this.props;
        if (value && value !== form.getFieldValue("password")) {
            callback(t("PasswordRepeatIncorrect"));
        } else {
            callback();
        }
    };

    validateToNextPassword = (rule: any, value: any, callback: Callback) => {
        const form = this.props.form;
        if (value && this.state.confirmDirty) {
            form.validateFields(["confirm"], { force: true });
        }
        callback();
    };

    handleConfirmBlur = (e: any) => {
        const value = e.target.value;
        this.setState({ confirmDirty: this.state.confirmDirty || !!value });
    };

    hasErrors(fieldsError: object) {
        const { form } = this.props;
        const password = form.getFieldValue("password");
        const confirm = form.getFieldValue("confirm");

        if (!password || password === "" || !confirm || confirm === "") {
            return true;
        } else {
            return Object.values(fieldsError).some((err) => err);
        }
    }

    render() {
        const { orgCookie } = this.props;
        const { getFieldDecorator, getFieldsError } = this.props.form;
        const { user, status } = this.state;
        const email = user ? user.email : t("ResetPassword_YourAccount");

        const PasswordStrengthPopover = (
            <div className={theme.passwordPopover}>
                <p>{t("PasswordRule1")}</p>
                <p>{t("PasswordRule2")}</p>
                <p>{t("PasswordRule3")}</p>
                <p>{t("PasswordRule4")}</p>
            </div>
        );

        if (status.type === "INVALID_TOKEN") {
            return (
                <FormCardContainer
                    rightSide={
                        <RightSideIcon
                            icon={linkBrokenSvg}
                            customImages={true}
                            orgCookie={orgCookie}
                        />
                    }
                >
                    <FormTitle title={t("ResetPassword_LinkIsIncorrect")} />
                    <FormSubtitle
                        subtitle={t("ResetPassword_LinkIsIncorrectSubtitle")}
                    />
                </FormCardContainer>
            );
        }

        return (
            <FormCardContainer
                rightSide={
                    <RightSideIcon
                        icon={passwordSvg}
                        customImages={true}
                        orgCookie={orgCookie}
                    />
                }
            >
                <Spin spinning={!user}>
                    <FormTitle title={t("ResetPassword_ResetPassword")} />
                    <FormSubtitle
                        subtitle={t("ResetPassword_Subtitle", { email })}
                    />
                    <FormCard onSubmitForm={this.handleSubmit}>
                        <FormItem>
                            {getFieldDecorator("password", {
                                rules: [
                                    { validator: this.handleConfirmPassword },
                                ],
                            })(
                                <Input
                                    type="password"
                                    placeholder={t("ResetPassword_Password")}
                                />
                            )}
                        </FormItem>
                        <FormItem>
                            {getFieldDecorator("confirm", {
                                rules: [
                                    { validator: this.compareToFirstPassword },
                                ],
                            })(
                                <Input
                                    type="password"
                                    placeholder={t(
                                        "ResetPassword_RepeatPassword"
                                    )}
                                    onBlur={this.handleConfirmBlur}
                                />
                            )}
                        </FormItem>
                        <FormItem className={theme.hcaptchaContainer}>
                            {getFieldDecorator("captcha", {
                                rules: [{ required: true }],
                            })(
                                <HCaptcha
                                    size="normal"
                                    sitekey={
                                        ENVIRONMENT.IMPERO_HCAPTCHA_SITE_KEY
                                    }
                                    onVerify={this.handleCaptchaVerify}
                                />
                            )}
                        </FormItem>
                        <Button
                            id="resetFormSubmitBtn"
                            variant="primary"
                            type="submit"
                            disabled={this.hasErrors(getFieldsError())}
                            loading={this.state.status.type === "LOGGING_IN"}
                            block
                        >
                            {t("ResetPassword_Button")}
                        </Button>
                    </FormCard>
                    <CardFooter>
                        <Popover
                            content={PasswordStrengthPopover}
                            placement="bottom"
                            trigger="click"
                        >
                            <Link to="/">{t("PasswordRules")}</Link>
                        </Popover>
                    </CardFooter>
                </Spin>
            </FormCardContainer>
        );
    }
    private handleCaptchaVerify = (captcha: string | null) => {
        this.props.form.setFieldsValue({
            captcha,
        });
    };
}

interface ResetPasswordProps extends FormComponentProps, WithRouterProps {
    password?: string;
    captcha?: string;
    ajaxManager: AjaxManager;
    orgCookie: OrgCookie;
}

interface User {
    email: string;
    lastName: string;
    firstName: string;
}

type ResetPasswordStatus =
    | { type: "DEFAULT" }
    | { type: "LOGGING_IN" }
    | { type: "INVALID_TOKEN" };
interface ResetPasswordState {
    status: ResetPasswordStatus;
    confirmDirty?: boolean;
    user?: User;
}

const ResetPassword = withRouter(
    Form.create<ResetPasswordProps>()(ResetPasswordInternal)
);

export default ResetPassword;
